import { useQueryClient } from '@tanstack/react-query'
import { TrashIcon } from 'lucide-react'
import React, { useState } from 'react'

import { useDeleteProofOfCoverage } from 'components/hooks/member_onboarding_v2/useDeleteProofOfCoverage'
import { useUploadProofOfCoverage } from 'components/hooks/useUploadProofOfCoverage'
import { FileList, FileUpload, IconButton } from 'slab'
import { getGetMemberOnboardingProofOfCoverageMostRecentQueryKey as proofOfCoverageQueryKey } from 'utils/web_api'
import { type MemberOnboardingProofOfCoverage } from 'utils/web_api.schemas'

import { useOnboarding } from '../OnboardingContext'

const FileUploadComponent = ({
  proofOfCoverage,
  planYear,
}: {
  proofOfCoverage: MemberOnboardingProofOfCoverage | undefined;
  planYear: string;
}) => {
  const [error, setError] = useState<string | null>(null)
  // Store the uploaded proof of coverage so we can display it
  // until the proofOfCoverage query is refetched after invalidation
  const [uploadedProofOfCoverage, setUploadedProofOfCoverage] = useState<MemberOnboardingProofOfCoverage | null>(null)
  const proofOfCoverageFile = (proofOfCoverage && !!proofOfCoverage.file_id.length) ? proofOfCoverage : uploadedProofOfCoverage
  const { refreshOnboardingInfo } = useOnboarding()

  const queryClient = useQueryClient()

  const uploadProofOfCoverage = useUploadProofOfCoverage({ planYear })
  const deleteProofOfCoverage = useDeleteProofOfCoverage({
    fileId: proofOfCoverageFile?.file_id ?? '',
  })

  const refreshProofOfCoverage = () => {
    queryClient.invalidateQueries({
      queryKey: proofOfCoverageQueryKey(),
    })
  }

  const onRemoveFile = () => {
    if (proofOfCoverageFile?.file_id) {
      deleteProofOfCoverage.mutate(undefined, {
        onSuccess: () => {
          setUploadedProofOfCoverage(null)
          refreshProofOfCoverage()
          refreshOnboardingInfo()
        },
        onError: () => {
          setError('Something went wrong deleting the file. Please try again.')
        },
      })
    }
  }

  const onFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList?.length) return false

    const newFile = fileList[0]
    uploadProofOfCoverage.mutate(
      {
        file: newFile,
      },
      {
        onSuccess: (data: MemberOnboardingProofOfCoverage) => {
          setUploadedProofOfCoverage({ file_id: data.file_id, file_name: newFile.name })
          refreshProofOfCoverage()
          refreshOnboardingInfo()
        },
        onError: () => {
          setError(
            'Something went wrong uploading the file. Please try again.'
          )
        },
      }
    )
  }

  return (
    <div>
      {!proofOfCoverageFile && uploadProofOfCoverage.status !== 'pending' && (
        <FileUpload
          multiple={false}
          error={error}
          accept='.pdf,.jpg,.jpeg,.png'
          onChange={onFileUpload}
        >
          <FileUpload.Content layout='horizontal' />
        </FileUpload>
      )}

      {!proofOfCoverageFile && uploadProofOfCoverage.status === 'pending' && (
        <FileList>
          <FileList.File
            name={uploadProofOfCoverage.variables?.file?.name ?? ''}
            status='uploading'
          />
        </FileList>
      )}

      {proofOfCoverageFile && (
        <FileList>
          <FileList.File
            name={proofOfCoverageFile.file_name}
            status='success'
            actions={
              <IconButton
                icon={TrashIcon}
                onClick={onRemoveFile}
                label='Remove'
                disabled={deleteProofOfCoverage.isPending}
              />
            }
          />
        </FileList>
      )}
    </div>
  )
}

export default FileUploadComponent
